import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@bevco/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField from "@insite/mobius/TextField";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "Phone";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form phone field widget. */
const DynamicPhoneNumber: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    const validate = React.useCallback(() => {
        const value = fieldValue == null ? "" : String(fieldValue).trim();
        let error = "";
        if (!value && isRequired) {
            error = siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage") as string;
        } else if (value && !validatePhone(value)) {
            error = siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage") as string;
        }
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, validate, formContext.validators]);

    return (
        <DynamicFormFieldShell>
            <TextField
                type="tel"
                label={label}
                required={isRequired}
                placeholder={hintText}
                value={fieldValue || ""}
                error={errorMessage}
                onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.target.value })}
                onBlur={() => validate()}
            />
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicPhoneNumber),
    definition: {
        group: widgetGroup,
        displayName: "Phone Number",
        icon: "phone",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
